import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockIcon, EyeIcon, EyeOffIcon, CheckIcon, XIcon } from "lucide-react";

interface PasswordInputProps {
  password: string;
  setPassword: (password: string) => void;
  label: string;
  placeholder: string;
  id: string; // Add id prop to make inputs unique
  showValidation?: boolean; // Optional prop to show validation
}

export const PasswordInput = ({ 
  password, 
  setPassword, 
  label,
  placeholder,
  id,
  showValidation = false
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  // Simple password validation checks
  const minLengthValid = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-gray-200">{label}</Label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <LockIcon className="h-4 w-4 text-blue-400" />
        </div>
        <Input
          id={id}
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="pl-10 pr-10 bg-gray-800/50 border-gray-700 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-300"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeOffIcon className="h-4 w-4" />
          ) : (
            <EyeIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Simple password validation feedback - only shown when showValidation is true and user has started typing */}
      {showValidation && password.length > 0 && (
        <div className="mt-2 text-xs grid grid-cols-2 gap-x-2 gap-y-1">
          <div className="flex items-center gap-1">
            {minLengthValid ? 
              <CheckIcon className="h-3 w-3 text-green-500" /> : 
              <XIcon className="h-3 w-3 text-red-500" />}
            <span className={minLengthValid ? "text-green-500" : "text-red-500"}>
              Min 8 characters
            </span>
          </div>
          <div className="flex items-center gap-1">
            {hasLowercase ? 
              <CheckIcon className="h-3 w-3 text-green-500" /> : 
              <XIcon className="h-3 w-3 text-red-500" />}
            <span className={hasLowercase ? "text-green-500" : "text-red-500"}>
              Lowercase letter
            </span>
          </div>
          <div className="flex items-center gap-1">
            {hasUppercase ? 
              <CheckIcon className="h-3 w-3 text-green-500" /> : 
              <XIcon className="h-3 w-3 text-red-500" />}
            <span className={hasUppercase ? "text-green-500" : "text-red-500"}>
              Uppercase letter
            </span>
          </div>
          <div className="flex items-center gap-1">
            {hasDigit ? 
              <CheckIcon className="h-3 w-3 text-green-500" /> : 
              <XIcon className="h-3 w-3 text-red-500" />}
            <span className={hasDigit ? "text-green-500" : "text-red-500"}>
              Number
            </span>
          </div>
          <div className="flex items-center gap-1">
            {hasSymbol ? 
              <CheckIcon className="h-3 w-3 text-green-500" /> : 
              <XIcon className="h-3 w-3 text-red-500" />}
            <span className={hasSymbol ? "text-green-500" : "text-red-500"}>
              Symbol
            </span>
          </div>
        </div>
      )}
    </div>
  );
};